import React, { useState } from 'react';
import { Spinner, Stack, Text, SpinnerSize, Label } from '@fluentui/react';
import { Field, Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import { PrimaryButton } from 'office-ui-fabric-react';
import * as Yup from 'yup';
import { useMutation } from 'react-query';
import moment from 'moment';
import ContractTypeAhead from '../../components/ContractTypeAhead';
import DatePicker from '../../components/DatePicker';
import theme from '../../theme.style';
import DebugContractHeader from './components/DebugContractHeader';
import DebugContractAdditionalDetails from './components/DebugContractAdditionalDetails';
import DebugRecommendations from './components/recommendations';
import * as sapi from './services/sapi';

const validationSchema = Yup.object().shape({
  contract: Yup.object({
    contractId: Yup.string().required('Required'),
  }).required(),
  month: Yup.date().required(),
});

function useContractDebugMutation() {
  return useMutation('driContractDebug', ({ contractId, month }) =>
    sapi.get(`/dri/contract/${contractId}/debug`, {
      params: {
        month: moment(month).format('YYYY-MM-DD'),
      },
    }),
  );
}

const startOfMonth = moment().startOf('month').toDate();

export default function DriContractDebugPage() {
  const debugContractMutation = useContractDebugMutation();
  const [monthDate, setMonthDate] = useState(startOfMonth);

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Stack tokens={{ childrenGap: 12 }}>
        <Text style={{ fontSize: '2rem' }}>Contract Debugger</Text>
        <Text style={{ fontWeight: 'normal' }}>
          The question that this page helps answer is &quot;did we ingest everything for this
          month?&quot;. If we did not ingest everything, this page will give you recommendations on
          which actions are best to take to resolve the various issues that may arise.
        </Text>
      </Stack>

      <Formik
        initialValues={{
          contract: {
            contractId: '',
          },
          month: startOfMonth,
        }}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async values => {
          await debugContractMutation.mutateAsync({
            contractId: values.contract.contractId,
            month: moment(monthDate).format(),
          });
        }}
      >
        {({ values, submitForm, errors }) => (
          <Form>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 20 }}>
              <Field
                name="contract"
                label="Contract"
                component={ContractTypeAhead}
                contractName={values.contract.name}
                rootStyle={{
                  flexGrow: 1,
                }}
                placeholder="Search Contracts"
                required
                handleCallback={() => {}}
              />

              <Stack>
                <Label>Month</Label>
                <Field
                  name="month"
                  component={DatePicker}
                  disabled={false}
                  required
                  showClearButton
                  showMonthPickerAsOverlay={false}
                  isMonthPickerVisible
                  defaultValue={startOfMonth.toISOString()}
                  value={monthDate}
                  formatDate={date => moment(date).format('MMM YYYY')}
                  onSelectDate={date => setMonthDate(moment(date).startOf('month').toDate())}
                  calendarProps={{
                    isDayPickerVisible: false,
                  }}
                  ariaLabel="month date"
                />
              </Stack>

              <PrimaryButton style={{ marginTop: isEmpty(errors) ? 25 : 6 }} onClick={submitForm}>
                {debugContractMutation.isLoading ? <Spinner size={SpinnerSize.xSmall} /> : 'Submit'}
              </PrimaryButton>
            </Stack>
          </Form>
        )}
      </Formik>

      {debugContractMutation.isLoading && (
        <Text style={{ fontWeight: 'normal' }}>Hang tight, this may take some time...</Text>
      )}
      {debugContractMutation.error && (
        <Text style={{ color: theme.palette.accentError }}>
          {debugContractMutation.error?.response?.data?.message ||
            debugContractMutation.error?.message}
        </Text>
      )}

      {debugContractMutation.data && (
        <Stack tokens={{ childrenGap: 24 }}>
          <DebugContractHeader data={debugContractMutation.data} />
          <DebugRecommendations data={debugContractMutation.data} />
          <DebugContractAdditionalDetails data={debugContractMutation.data} />
        </Stack>
      )}
    </Stack>
  );
}
